// Soft-Markov mood machine: dwell timers, weighted transitions (no self), smoothstep crossfade.
// Pure: randomness via injected Rng.

import type { AxisVector, Mood } from '@shared/parameterState';
import { MOODS } from '@shared/parameterState';
import type { Rng } from '@shared/rng';
import { nextRange, weightedPick } from '@shared/rng';
import {
  MOOD_TARGETS,
  TRANSITION_MATRIX,
  DWELL_RANGE_SEC,
  lerpVector,
  cloneVector,
} from './moods';
import { smoothstep } from './smoothing';

/**
 * Draw the next mood given the current one and a roaming amount (0 sticky, 1 explore).
 * roaming mixes the transition row toward uniform over the other moods.
 */
export function drawNextMood(rng: Rng, current: Mood, roaming = 0): Mood {
  const others = MOODS.filter((m) => m !== current);
  const row = TRANSITION_MATRIX[current];
  const baseWeights = others.map((m) => row[m] ?? 0);
  const uniform = 1 / others.length;
  const mix = 0.6 * Math.max(0, Math.min(1, roaming));
  const weights = baseWeights.map((w) => w + (uniform - w) * mix);
  return weightedPick(rng, others, weights);
}

export class MoodMachine {
  current: Mood;
  next: Mood | null = null;
  dwellRemainingSec: number;
  crossfadeTotalSec = 0;
  crossfadeElapsedSec = 0;
  /** Frozen "from" vector captured at transition start (so it does not move under us). */
  private fromVec: AxisVector;
  private toVec: AxisVector;
  roaming = 0;

  constructor(rng: Rng, initial: Mood = 'tidepool') {
    this.current = initial;
    this.fromVec = cloneVector(MOOD_TARGETS[initial]);
    this.toVec = cloneVector(MOOD_TARGETS[initial]);
    this.dwellRemainingSec = this.drawDwell(rng, initial);
  }

  private drawDwell(rng: Rng, mood: Mood): number {
    const [lo, hi] = DWELL_RANGE_SEC[mood];
    return nextRange(rng, lo, hi);
  }

  /** The dominant mood used for articulation picks (excitation/wild/decay). */
  get articulationMood(): Mood {
    if (this.next && this.blend >= 0.5) return this.next;
    return this.current;
  }

  get blend(): number {
    if (!this.next) return 0;
    if (this.crossfadeTotalSec <= 0) return 1;
    return smoothstep(this.crossfadeElapsedSec / this.crossfadeTotalSec);
  }

  forceMood(rng: Rng, mood: Mood): void {
    this.current = mood;
    this.next = null;
    this.fromVec = cloneVector(MOOD_TARGETS[mood]);
    this.toVec = cloneVector(MOOD_TARGETS[mood]);
    this.crossfadeElapsedSec = 0;
    this.crossfadeTotalSec = 0;
    this.dwellRemainingSec = this.drawDwell(rng, mood);
  }

  step(dt: number, rng: Rng): void {
    if (this.next === null) {
      this.dwellRemainingSec -= dt;
      if (this.dwellRemainingSec <= 0) {
        // Start a transition.
        this.next = drawNextMood(rng, this.current, this.roaming);
        this.fromVec = cloneVector(MOOD_TARGETS[this.current]);
        this.toVec = cloneVector(MOOD_TARGETS[this.next]);
        this.crossfadeTotalSec = nextRange(rng, 30, 90);
        this.crossfadeElapsedSec = 0;
      }
    } else {
      this.crossfadeElapsedSec += dt;
      if (this.crossfadeElapsedSec >= this.crossfadeTotalSec) {
        this.current = this.next;
        this.next = null;
        this.fromVec = cloneVector(MOOD_TARGETS[this.current]);
        this.toVec = cloneVector(MOOD_TARGETS[this.current]);
        this.crossfadeElapsedSec = 0;
        this.crossfadeTotalSec = 0;
        this.dwellRemainingSec = this.drawDwell(rng, this.current);
      }
    }
  }

  /** Crossfaded target vector at the current blend. */
  currentMoodTarget(): AxisVector {
    if (!this.next) return cloneVector(this.fromVec);
    return lerpVector(this.fromVec, this.toVec, this.blend);
  }

  /** For ParameterState.mood. */
  moodState(): { from: Mood; to: Mood; blend: number } {
    return {
      from: this.current,
      to: this.next ?? this.current,
      blend: this.blend,
    };
  }
}
